import {createSelector} from 'reselect';

import {getTranslations, getListingPage} from './store';

function isString(el) {
    return typeof el === 'string';
}

function findPriceItems(pricingInfo, type, subType) {
    let priceItems = [];

    if (pricingInfo && pricingInfo.items) {
        const pricingInfoItems = pricingInfo.items.filter((item) => {
            if (isString(type) && !isString(subType)) {
                return item.type === type;
            } else if (isString(subType) && !isString(type)) {
                return item.subType === subType;
            } else if (isString(type) && isString(subType)) {
                return item.type === type && item.subType === subType;
            }
            return false;
        });

        for (let quoteItem of pricingInfoItems) {
            let priceItem = {
                label: quoteItem.label,
                amount: quoteItem.amount.localized
            };

            priceItems.push(priceItem);
        }
    }

    return priceItems;
}

// This is the priceDetails from the graphql-traveler-checkout partials.
// This note is important until we remove all the PDS related details.
const getPriceDetails = createSelector(
    [getListingPage],
    (listingPage) => {
        // We store the object returned from graphql into priceDetails.
        // It defaults to a POO, which doesn't have toJS.
        // This object contains errors, priceDetails and endTime.
        const priceDetails =
            listingPage.priceDetails && listingPage.priceDetails.toJS
                ? listingPage.priceDetails.toJS()
                : listingPage.priceDetails || {};
        // Return just the priceDetails data.
        // This is used to determine avg nightly, total, and is the chunk of
        // data passed directly to the PriceDetails component.
        return priceDetails.priceDetails || {};
    }
);

const getAverageNightlyAmountByType = (priceDetails = {averageNightlies: []}, type) => {
    return (
        priceDetails.averageNightlies.find((currentTotal) => currentTotal.type === type) || {
            perNightCost: {localized: ''}
        }
    ).perNightCost.localized;
};

const getAverageNightly = createSelector(
    [getPriceDetails],
    (priceDetails) => {
        let averageNightlyBase = '';
        let averageNightlyTotalRent = '';
        let averageNightlyTotalTrip = '';

        if (priceDetails.averageNightlies) {
            averageNightlyBase = getAverageNightlyAmountByType(priceDetails, 'BASE_RENT');
            averageNightlyTotalRent = getAverageNightlyAmountByType(priceDetails, 'TOTAL_RENT');
            averageNightlyTotalTrip = getAverageNightlyAmountByType(priceDetails, 'TOTAL_TRIP');
        }

        return {
            averageNightlyBase,
            averageNightlyTotalRent,
            averageNightlyTotalTrip
        };
    }
);

const getTotals = createSelector(
    [getPriceDetails],
    (priceDetails) => {
        return {
            totalWithoutDeposits:
                priceDetails.totals && priceDetails.totals[0] ? priceDetails.totals[0].amount : '',
            totalWithDeposits: ''
        };
    }
);

const getFirstPayment = createSelector(
    [getPriceDetails, getTranslations],
    (priceDetails, trans) => {
        if (!priceDetails.dueNow || priceDetails.dueNow.length < 2) {
            return null;
        }

        const dueNow = priceDetails.dueNow.find((dn) => dn.amount);
        const amount = dueNow && dueNow.amount;

        if (!amount) {
            return null;
        }

        return {
            amount,
            label: trans.quoteFirstPayment,
            tooltipContent: trans.payTheRestPopoverManager
        };
    }
);

const getConditionalInstantBook = createSelector(
    [getPriceDetails],
    (priceDetails) => !!priceDetails.instantBooking
);

const getPriceLabels = createSelector(
    [getTranslations],
    (trans) => ({
        taxesAndFees: trans.taxesAndFees,
        total: trans.total,
        viewDetails: trans.viewDetails
    })
);

export {
    findPriceItems,
    getAverageNightly,
    getConditionalInstantBook,
    getPriceDetails,
    getPriceLabels,
    getTotals,
    getFirstPayment
};
